<template>
  <div>
    <div class="tw-flex tw-justify-between">
      <div class="tw-text-xl tw-font-light tw-my-auto">Plans</div>
      <div class="tw-flex">
        <base-button type="text" size="sm" @click="openFilters()">
          <template #icon>
            <filter-icon />
          </template>
          Filters
        </base-button>
        <export-button
          class="tw-ml-5"
          size="sm"
          type="primary"
          label="Export"
          :filename="`PredictilePlans.csv`"
          :requestPayload="requestPayload"
          :url="`/businesses/${businessId}/reports/plans/export`"
          @onError="didErrorExport"
        >
        </export-button>
      </div>
    </div>
    <filter-display class="tw-mt-3" :filters="filters" />
    <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-4 tw-gap-4">
      <aggregate-list
        class="sm:tw-col-span-2 summarylist"
        :class="{ 'tw-overflow-y-scroll': locationAggregates.length > 6 }"
        :aggregates="locationAggregates"
        :isLoading="isLoading"
        labelKey="name"
        idKey="businessLocationId"
        valueKey="count"
        title="Locations"
      />
      <aggregate-list
        class="summarylist"
        :class="{
          'tw-overflow-y-scroll': businessPlanAggregates.length > 6,
        }"
        :aggregates="businessPlanAggregates"
        :isLoading="isLoading"
        labelKey="businessPlanName"
        idKey="businessPlanId"
        valueKey="count"
        title="Plans"
      />
      <aggregate-list
        class="summarylist"
        :class="{ 'tw-overflow-y-scroll': statusAggregates.length > 6 }"
        :aggregates="statusAggregates"
        :isLoading="isLoading"
        labelKey="status"
        idKey="status"
        valueKey="count"
        title="Status"
      />
    </div>
    <div class="tw-mt-6">{{ page.total }} plans</div>
    <base-table :loading="isLoading" :fields="fields" :items="plans" :sort-key.sync="sortKey" :sort-desc.sync="sortDesc" :loadingRows="5" class="tw-mt-2">
      <template #member="{item: member}">
        <div class="tw-flex">
          <base-button type="text" size="md" @click="view(member.userId)">
            <span class="tw-whitespace-nowrap tw-text-left">{{ member.fullName }}</span>
          </base-button>
        </div>
      </template>
      <template #plan="{item: member}">
        <div class="tw-whitespace-nowrap">{{ member.businessPlanName }}</div>
        <div class="tw-whitespace-nowrap tw-text-gray-500 tw-text-xs">{{ member.planGroup }}</div>
      </template>
      <template #status="{item: member}">
        <div class="tw-text-gray-700 tw-font-normal tw-whitespace-nowrap">{{ capitalize(member.status) }}</div>
      </template>
      <template #address="{item: member}">
        <div class="tw-text-gray-700 tw-text-xs tw-whitespace-nowrap">{{ address(member.addressLine1, member.addressLine2) }}</div>
        <div class="tw-text-gray-700 tw-text-xs tw-whitespace-nowrap">{{ member.city + ", " + member.state + " " + member.postalCode }}</div>
      </template>
      <template #phone="{item: member}">
        <base-phone-number-field class="" :value="member.phoneNumber" />
      </template>
      <template #email="{item: member}">
        <div class="tw-whitespace-nowrap">{{ member.email }}</div>
      </template>
      <template #price="{item: member}">
        <div class="tw-whitespace-nowrap">{{ formatCyclePrice(member) }}</div>
      </template>
      <template #credits="{item: member}">
        <div class="tw-whitespace-nowrap">{{ member.itemCategoryCredits[0].balance }}</div>
      </template>
      <template #startDate="{item: member}">
        <div class="tw-text-sm tw-text-gray-700 tw-font-normal tw-whitespace-nowrap">{{ formatDate(member.startDate) }}</div>
      </template>
      <template #cycleDate="{item: member}">
        <div class="tw-text-sm tw-text-gray-700 tw-font-normal tw-whitespace-nowrap">{{ member.cycleDate ? formatDate(member.cycleDate) : "-" }}</div>
      </template>
      <template #renewDate="{item: member}">
        <div class="tw-text-sm tw-text-gray-700 tw-font-normal tw-whitespace-nowrap">{{ formatDate(member.lastUpdatedDate) }}</div>
      </template>
      <template #location="{item: member}">
        <div class="tw-flex">
          <div>
            <div class="tw-text-sm tw-text-gray-700 tw-font-normal tw-whitespace-nowrap">
              {{ member.businessLocationName }}
            </div>
          </div>
        </div>
      </template>
      <template #empty-state>
        <div class="tw-flex tw-justify-center tw-py-5">
          No Results
        </div>
      </template>
      <template #loading-row>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-24" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-12" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-48" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-56" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-24" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-24" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-24" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-24" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-24" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-24" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-24" />
        </td>
      </template>
    </base-table>
    <base-pagination v-model="page" class="tw-mt-2" />
    <report-filter-slideover
      ref="filterSlideOver"
      :business-id="businessId"
      date-filter-label="Created"
      :show-programs-filter="false"
      :show-program-groups-filter="false"
      :show-prescription-status-filter="false"
      :showPatientStateFilter="true"
      :showSelfOnBoardingFilter="true"
      @filter-updated="applyFilter"
    />
  </div>
</template>

<script>
import ReportFilterSlideover from "@/views/business/reports/components/ReportFilterSlideover";
import FilterDisplay from "@/views/business/reports/components/FilterDisplay";
import AggregateList from "@/views/business/reports/components/AggregateList";
import { FilterIcon } from "@/assets/svgs";
import { mapGetters } from "vuex";
import moment from "moment";
import { capitalize } from "lodash";
import ExportButton from "@/components/ExportButton.vue";

const TAKE_KEY = "predictile_plans_report_take";

export default {
  name: "MemberPlansReport",
  components: {
    ReportFilterSlideover,
    FilterDisplay,
    AggregateList,
    FilterIcon,
    ExportButton,
  },
  props: {
    businessId: String,
  },
  data: function() {
    return {
      plans: [],
      page: {
        take: localStorage.getItem(TAKE_KEY) ? +localStorage.getItem(TAKE_KEY) : 50,
        skip: 0,
        total: 0,
      },
      isLoading: false,
      filters: {
        startDate: null,
        endDate: null,
        locationsFilter: [],
        plansFilter: [],
        planGroupsFilter: [],
        planStatusFilter: [],
        statesFilter: [],
        selfOnBoardingFilter: [],
      },
      sortKey: "startDate",
      sortDesc: true,
      statusAggregates: [],
      businessPlanAggregates: [],
      locationAggregates: [],
    };
  },
  computed: {
    ...mapGetters("organization", ["organization", "cobrandId"]),
    fields() {
      let columns = [];

      columns.push({ title: this.organization.customerLabelSingular, key: "member", isSortable: true });
      columns.push({ title: "Plan", key: "plan", isSortable: true });
      columns.push({ title: "Location", key: "location", isSortable: true });
      columns.push({ title: "Status", key: "status", isSortable: true });
      columns.push({ title: "Address", key: "address", isSortable: false });
      if (this.organization.useCreditsSetting === true) columns.push({ title: "Credit Balance", key: "credits" });
      columns.push({ title: "Phone", key: "phone" });
      columns.push({ title: "Email", key: "email" });
      columns.push({ title: "Price", key: "price" });
      columns.push({ title: "Started", key: "startDate", isSortable: true });
      columns.push({ title: "Next Cycle", key: "cycleDate", isSortable: true, thClass: "tw-whitespace-nowrap" });
      columns.push({ title: "Renews", key: "renewDate", isSortable: true });

      return columns;
    },
    requestPayload() {
      let locationFilterStrings = this.filters.locationsFilter
        ? this.filters.locationsFilter.map(function(item) {
            return item["businessLocationId"];
          })
        : [];
      let planFilterStrings = this.filters.plansFilter
        ? this.filters.plansFilter.map(function(item) {
            return item["businessPlanId"];
          })
        : [];
      let statesFiltervalues = this.filters.statesFilter
        ? this.filters.statesFilter.map(function(item) {
            return item["value"];
          })
        : [];

      return {
        startDate: this.filters.startDate,
        endDate: this.filters.endDate,
        businessLocationIds: locationFilterStrings,
        planGroups: this.filters.planGroupsFilter,
        businessPlanIds: planFilterStrings,
        statuses: this.filters.planStatusFilter,
        states: statesFiltervalues,
        sortColumn: this.sortKey,
        sortOrder: this.sortDesc === true ? "desc" : "asc",
        selfOnBoarding: this.filters.selfOnBoardingFilter,
      };
    },
  },
  watch: {
    page(newValue) {
      localStorage.setItem(TAKE_KEY, newValue.take);
      this.loadPage();
    },
    sortKey() {
      this.loadPage();
    },
    sortDesc() {
      this.loadPage();
    },
  },
  created() {
    this.loadPage();
  },
  methods: {
    didErrorExport(err) {
      this.EventBus.$emit("GLOBAL_ERROR", {
        title: "Failed to download CSV",
        message: "We had a problem downloading the report. Please reload the page to try again.",
        error: err,
      });
    },
    capitalize,
    address(line1, line2) {
      return line2 === null ? line1 : line1 + ", " + line2;
    },
    openFilters() {
      this.$refs.filterSlideOver.show();
    },
    applyFilter(filters) {
      this.filters = filters;
      this.loadPage();
    },
    next() {
      this.page.skip += this.page.take;
    },
    previous() {
      this.page.skip -= this.page.take;
    },
    loadPage() {
      this.isLoading = true;
      let url = `/businesses/${this.businessId}/reports/plans/search?skip=${this.page.skip}&take=${this.page.take}`;

      this.$axios
        .post(url, this.requestPayload)
        .then((res) => {
          this.plans = res.data.page.items;
          this.page.total = res.data.page.total;
          this.statusAggregates = res.data.statusAggregate;
          this.businessPlanAggregates = res.data.businessPlanAggregate;
          this.locationAggregates = res.data.locationAggregate;
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Failed to load prescriptions",
            message: "We had a problem retrieving the list of prescriptions. Please reload the page to try again.",
            error: err,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    formatDate(date) {
      return new moment.utc(date).local().format("M/D/YYYY");
    },
    formatCyclePrice(plan) {
      plan.formattedPrice =
        new Intl.NumberFormat(window.navigator.language, { style: "currency", currency: plan.currency }).format(plan.priceInCents / 100) +
        " every " +
        plan.cycleFrequency +
        " " +
        plan.cycleInterval;

      if (plan.cycleFrequency > 1) plan.formattedPrice += "s";
      plan.formattedPrice = plan.formattedPrice.replace("every 1", "/");

      return plan.formattedPrice;
    },
    view(userId) {
      window.open(`/businesses/${this.businessId}/members/${userId}/plans`, "_blank");
    },
  },
};
</script>
<style scoped>
.summarylist {
  max-height: 247px;
}
</style>
